// The UI writes the URL directly in places — history.pushState/replaceState
// from the workspace stores (a tab click writes ?tab=, closing a project drops
// ?project=). Next.js's router followed those calls (Next 14.1+), so
// useSearchParams saw them. React Router does not: it only learns of a URL
// change through its own navigate() or a popstate. Without this, a tab click
// changes the address bar and nothing else.
//
// installHistorySync wraps the two methods once, before the router is made.
// A call from the router itself carries its own state ({ key, idx }) and is
// left alone. Any other call keeps the router's bookkeeping in the entry
// (key, and idx +1 for a push) with the caller's data as `usr`, then fires a
// popstate so the router reads the new location — catching up without adding
// an entry of its own.

type RouterState = { usr: unknown; key: string; idx: number };

function isRouterState(data: unknown): data is RouterState {
  return typeof data === "object" && data !== null && "key" in data && "idx" in data;
}

let installed = false;

export function installHistorySync(win: Window = window): void {
  if (installed) return;
  installed = true;
  const history = win.history;
  for (const method of ["pushState", "replaceState"] as const) {
    const original = history[method].bind(history);
    history[method] = (data: unknown, unused: string, url?: string | URL | null) => {
      if (isRouterState(data)) {
        original(data, unused, url);
        return;
      }
      const current: unknown = history.state;
      const idx = isRouterState(current) ? current.idx : 0;
      const state: RouterState = {
        usr: data,
        key: Math.random().toString(36).slice(2, 10),
        idx: method === "pushState" ? idx + 1 : idx,
      };
      original(state, unused, url);
      win.dispatchEvent(new PopStateEvent("popstate", { state }));
    };
  }
}

/** Test seam: lets a test install against a fresh window. */
export function resetHistorySyncForTest(): void {
  installed = false;
}
